import { TaxType } from './types';

export interface IAppliedTax {
    readonly taxRateId: number;
    readonly name: string;
    readonly rate: number;
    readonly type: TaxType;
    readonly taxableBaseAmount: number;
    readonly amountCalculated: number;
}

export interface IAppliedTaxData {
    taxRateId: number;
    name: string;
    rate?: number;
    type?: TaxType;
    taxableBaseAmount?: number;
    amountCalculated?: number;
}

export class AppliedTax implements IAppliedTax {
    readonly taxRateId: number;
    readonly name: string;
    readonly rate: number;
    readonly type: TaxType;
    readonly taxableBaseAmount: number;
    readonly amountCalculated: number;

    constructor(data: IAppliedTaxData) {
        this.taxRateId = data.taxRateId;
        this.name = data.name;
        this.rate = data.rate ?? 0;
        this.type = data.type ?? 'none';
        this.taxableBaseAmount = data.taxableBaseAmount ?? 0;
        this.amountCalculated = data.amountCalculated ?? 0;
    }

    toJSON(): IAppliedTax {
        return {
            taxRateId: this.taxRateId,
            name: this.name,
            rate: this.rate,
            type: this.type,
            taxableBaseAmount: this.taxableBaseAmount,
            amountCalculated: this.amountCalculated,
        };
    }

    static fromJSON(data: Record<string, unknown>): AppliedTax {
        if (typeof data.taxRateId !== 'number') {
            throw new Error("AppliedTax.fromJSON: taxRateId must be a number");
        }
        if (typeof data.name !== 'string') {
            throw new Error("AppliedTax.fromJSON: name must be a string");
        }

        const rawType = data.type as string;
        // Normalize legacy tax type values
        const normalizedType = rawType === 'percent' ? 'percentage' : rawType;
        // Validate tax type with graceful fallback
        let validType: TaxType = 'none';
        if (normalizedType && ['percentage', 'fixed', 'none'].includes(normalizedType)) {
            validType = normalizedType as TaxType;
        } else if (normalizedType) {
            // Log warning for invalid tax type (data quality issue)
            if (typeof console !== 'undefined') {
                console.warn(
                    `AppliedTax.fromJSON: invalid tax type "${normalizedType}" for tax "${data.name}". Using default: "none". Run data migration to fix.`
                );
            }
        }

        return new AppliedTax({
            taxRateId: data.taxRateId,
            name: data.name,
            rate: typeof data.rate === 'number' ? data.rate : 0,
            type: validType,
            taxableBaseAmount: typeof data.taxableBaseAmount === 'number' ? data.taxableBaseAmount : 0,
            amountCalculated: typeof data.amountCalculated === 'number' ? data.amountCalculated : 0,
        });
    }
}
